import { motion } from "framer-motion";
import { Check, Star, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { useEffect, useState, useRef } from "react";

const plans = [
  {
    name: "Inicia Fit",
    normalPrice: "100.000",
    offerPrice: "75.000",
    featured: false,
    features: [
      "Planificación Mensual",
      "1 Clase Semanal",
      "4 Clases Mensuales",
      "Evaluación física mensual",
      "Actualización del programa de entrenamiento",
    ],
  },
  {
    name: "Fitness",
    normalPrice: "160.000",
    offerPrice: "135.000",
    featured: true,
    badge: "Recomendado",
    features: [
      "Planificación Mensual",
      "2 Clases Semanales",
      "8 Clases Mensuales",
      "Evaluación física mensual",
      "Actualización del programa de entrenamiento",
    ],
  },
  {
    name: "Vip Fitness",
    normalPrice: "200.000",
    offerPrice: "165.000",
    featured: false,
    features: [
      "Planificación Mensual",
      "3 Clases Semanales",
      "12 Clases Mensuales",
      "4 sesiones presoterapia",
      "Evaluación física mensual",
      "Actualización del programa de entrenamiento",
    ],
  },
];

// Animated Price Counter
const AnimatedPrice = ({ price }) => {
  const [displayPrice, setDisplayPrice] = useState("0");
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);
  const numericPrice = parseInt(price.replace(/\./g, ""));

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let start = 0;
          const duration = 1500;
          const increment = numericPrice / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= numericPrice) {
              setDisplayPrice(price);
              clearInterval(timer);
            } else {
              setDisplayPrice(Math.floor(start).toLocaleString("es-CL"));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [numericPrice, price, hasAnimated]);

  return <span ref={ref}>${displayPrice}</span>;
};

export const PricingSection = () => {
  const whatsappUrl = (planName) => 
    `https://wa.me/56966951278?text=${encodeURIComponent(`¡Hola! Me interesa el Plan ${planName} de Inspirion GYM. ¿Me pueden dar más información?`)}`;

  return (
    <section 
      id="planes"
      data-testid="pricing-section"
      className="relative py-24 bg-black overflow-hidden"
    >
      {/* Animated background */}
      <motion.div 
        className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-neon-magenta via-neon-green to-neon-cyan"
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      />
      
      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-neon-green rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.span 
            className="text-neon-magenta text-sm font-bold uppercase tracking-widest"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Inversión en tu salud
          </motion.span>
          <motion.h2 
            className="mt-4 text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, type: "spring" }}
          >
            <span className="text-neon-magenta text-glow-magenta">Planes</span>
            <br />
            <span className="text-neon-green text-glow-green">Personalizados</span>
          </motion.h2>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ 
                delay: index * 0.2,
                duration: 0.6,
                type: "spring"
              }}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.3 }
              }}
              className={`relative flex flex-col ${plan.featured ? 'md:-mt-4 md:mb-4' : ''}`}
            >
              {/* Featured Badge */}
              {plan.featured && (
                <motion.div 
                  className="absolute -top-4 left-1/2 -translate-x-1/2 z-20"
                  initial={{ opacity: 0, y: -20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5, type: "spring" }}
                >
                  <motion.div 
                    className="flex items-center gap-2 px-4 py-2 bg-neon-green text-black font-bold text-sm uppercase tracking-wider"
                    animate={{ 
                      boxShadow: [
                        "0 0 20px rgba(0, 255, 0, 0.5)",
                        "0 0 40px rgba(0, 255, 0, 0.8)",
                        "0 0 20px rgba(0, 255, 0, 0.5)",
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Star className="w-4 h-4" fill="currentColor" />
                    {plan.badge}
                  </motion.div>
                </motion.div>
              )}

              <motion.div 
                className={`flex-1 flex flex-col p-8 border-2 transition-all duration-300 ${
                  plan.featured 
                    ? 'bg-dark-card border-neon-green' 
                    : 'bg-dark-paper border-white/10'
                }`}
                whileHover={{
                  borderColor: plan.featured ? "#00FF00" : "rgba(255,255,255,0.3)",
                  boxShadow: plan.featured 
                    ? "0 0 40px rgba(0, 255, 0, 0.3)"
                    : "0 0 30px rgba(255, 255, 255, 0.1)"
                }}
              >
                {/* Plan Header */}
                <div className="text-center mb-6 pt-4">
                  <p className="text-gray-400 text-sm uppercase tracking-wider mb-1">Plan</p>
                  <motion.h3 
                    className={`text-3xl font-black uppercase italic ${
                      plan.featured ? 'text-neon-green text-glow-green' : 'text-white'
                    }`}
                    whileHover={{ scale: 1.05 }}
                  >
                    {plan.name}
                  </motion.h3>
                </div>

                {/* Features */}
                <ul className="flex-1 space-y-3 mb-8">
                  {plan.features.map((feature, fIndex) => (
                    <motion.li 
                      key={fIndex} 
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 + fIndex * 0.05 }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.2, rotate: 10 }}
                      >
                        <Check 
                          className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                            plan.featured ? 'text-neon-green' : 'text-neon-cyan'
                          }`} 
                        />
                      </motion.div>
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </motion.li>
                  ))}
                </ul>

                {/* Pricing */}
                <div className="text-center border-t border-white/10 pt-6">
                  <p className="text-sm text-gray-500 uppercase tracking-wider mb-2">
                    Precio Oferta:
                  </p>
                  <div 
                    className={`text-4xl md:text-5xl font-black ${
                      plan.featured 
                        ? 'text-neon-green text-glow-green' 
                        : 'text-white'
                    }`}
                  >
                    <AnimatedPrice price={plan.offerPrice} />
                  </div>
                  <div className="mt-2">
                    <span className="text-gray-500 text-sm uppercase tracking-wider">Valor Normal: </span>
                    <span className="text-gray-400 line-through">${plan.normalPrice}</span>
                  </div>
                </div>

                {/* CTA Button */}
                <motion.div 
                  className="mt-6"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    asChild
                    data-testid={`pricing-cta-${plan.name.toLowerCase().replace(' ', '-')}`}
                    className={`w-full font-bold text-base py-6 rounded-none uppercase tracking-wide transition-all duration-300 ${
                      plan.featured 
                        ? 'bg-neon-green hover:bg-neon-green/90 text-black' 
                        : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                    }`}
                    style={plan.featured ? { boxShadow: "0 0 20px rgba(0, 255, 0, 0.4)" } : {}}
                  >
                    <a href={whatsappUrl(plan.name)} target="_blank" rel="noopener noreferrer">
                      {plan.featured ? 'Elegir Plan' : 'Consultar'}
                    </a>
                  </Button>
                </motion.div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mt-12 text-center space-y-6"
        >
          {/* Clase de Prueba */}
          <motion.div
            className="inline-flex flex-col sm:flex-row items-center gap-4 px-8 py-5 bg-dark-card border-2 border-neon-cyan/30"
            whileHover={{ 
              borderColor: "rgba(0, 255, 255, 0.6)",
              boxShadow: "0 0 30px rgba(0, 255, 255, 0.3)"
            }}
          >
            <div className="text-center sm:text-left">
              <span className="text-neon-cyan text-sm font-bold uppercase tracking-wider block">
                ¿Quieres probar primero?
              </span>
              <span className="text-white text-lg font-bold">
                Clase de Prueba
              </span>
            </div>
            <Button
              asChild
              className="bg-neon-cyan hover:bg-neon-cyan/90 text-black font-bold px-6 py-3 rounded-none uppercase tracking-wide"
            >
              <a href={whatsappUrl("Clase de Prueba")} target="_blank" rel="noopener noreferrer">
                Agendar Prueba
              </a>
            </Button>
          </motion.div>

          <motion.div 
            className="inline-flex items-center gap-3 px-6 py-3 bg-dark-card border border-white/10"
            whileHover={{ 
              borderColor: "rgba(255, 0, 255, 0.3)",
              boxShadow: "0 0 20px rgba(255, 0, 255, 0.2)"
            }}
          >
            <Sparkles className="w-5 h-5 text-neon-magenta" />
            <span className="text-gray-300 text-sm">
              Acceso a descuentos exclusivos de servicios adicionales
            </span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
